import { assign, createActor, enqueueActions, setup } from "xstate";
import { fromStream } from "mobx-utils";
import { Step } from "../steps/v2/step";
import { Flow, FlowPosition, FlowState, Section } from "./types";
import { FlowFlexibility } from "@common/schemas";

export interface FlowStateMachineInput {
  flow: Flow;
  flowFlexibility: FlowFlexibility;
  initialFlowState: FlowState;
}

export interface FlowStateMachineContext extends FlowPosition {
  flow: Flow;
  flowFlexibility: FlowFlexibility;
  currentTime: number;
  eventQueue: FlowStateMachineEvent[];
}

const getFlowPosition = (context: FlowStateMachineContext): FlowPosition => ({
  currentSectionIndex: context.currentSectionIndex,
  currentStepIndex: context.currentStepIndex,
  flowKey: context.flow.key,
});

export interface FlowStateMachineHooks {
  onStepTransition?: (pos: FlowPosition, step: Step) => void;
  onWaitBlockingStep?: (pos: FlowPosition, step: Step) => void;
  onSectionTransition?: (pos: FlowPosition, section: Section) => void;
  onFlowFinish?: () => void;
  onStepRun?: (pos: FlowPosition, step: Step) => void;
  onStepDispose?: (pos: FlowPosition, step: Step) => void;
}

export interface CreateFlowStateActorParams {
  input: FlowStateMachineInput;
  hooks?: FlowStateMachineHooks;
}

type FlowStateMachineEvent =
  | { type: "unblock" }
  | { type: "mediaFinished" }
  | { type: "timeUpdate"; currentTime: number }
  | { type: "mediaPlaying" }
  | { type: "replay" };

export const createFlowStateActor = ({ input, hooks }: CreateFlowStateActorParams) => {
  const flowStateMachine = setup({
    types: {
      input: {} as FlowStateMachineInput,
      context: {} as FlowStateMachineContext,
      events: {} as FlowStateMachineEvent,
    },
    guards: {
      isLastSection: ({ context }) => {
        const { currentSectionIndex, flow } = context;
        return currentSectionIndex === flow.sections.length - 1;
      },
      isStepRunTime: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow, currentTime } = context;
        const section = flow.sections[currentSectionIndex];
        const step = section.steps[currentStepIndex];
        return currentTime >= step.start;
      },
      isStepEndTime: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow, currentTime } = context;
        const section = flow.sections[currentSectionIndex];
        const step = section.steps[currentStepIndex];
        return currentTime >= step.end;
      },
      hasNextStep: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];
        return currentStepIndex < section.steps.length - 1;
      },
      isLastStepInSection: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];
        return currentStepIndex === section.steps.length - 1;
      },
      isBlockingStep: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];
        const step = section.steps[currentStepIndex];
        return step.isBlocking;
      },
    },
    actions: {
      proceedNextSection: assign(({ context }) => {
        const { currentSectionIndex } = context;
        const nextSectionPosition = { sectionIndex: currentSectionIndex + 1, stepIndex: 0 };
        const section = context.flow.sections[nextSectionPosition.sectionIndex];
        hooks?.onSectionTransition?.(getFlowPosition(context), section);
        return {
          ...context,
          currentSectionIndex: nextSectionPosition.sectionIndex,
          currentStepIndex: nextSectionPosition.stepIndex,
          currentTime: 0,
        };
      }),
      proceedNextStep: assign(({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];

        if (currentStepIndex < section.steps.length - 1) {
          return { ...context, currentStepIndex: currentStepIndex + 1 };
        }

        return context;
      }),
      setCurrentTime: assign(({ context, event }) => {
        if (event.type === "timeUpdate") {
          return { ...context, currentTime: event.currentTime };
        }
        return context;
      }),
      deferMediaFinishedEvent: assign({
        eventQueue: ({ context: { eventQueue }, event }) => [...eventQueue, event],
      }),
      drainDeferredMediaFinishedEvents: enqueueActions(({ context: { eventQueue }, enqueue }) => {
        eventQueue.forEach((event) => enqueue.raise(event));
        enqueue.assign({ eventQueue: [] });
      }),
      // hooks
      initStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        hooks?.onStepTransition?.(getFlowPosition(context), step);
        step.init();
      },
      runStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        step.run();
        hooks?.onStepRun?.(getFlowPosition(context), step);
      },
      finishStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        step.finish();
      },
      disposeStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        step.dispose();
        hooks?.onStepDispose?.(getFlowPosition(context), step);
      },
      finish: () => {
        hooks?.onFlowFinish?.();
      },
      waitBlockingStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        hooks?.onWaitBlockingStep?.(getFlowPosition(context), step);
      },
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QDMA2B7A7gZQC4ENcwBiXASwFswBVABwkLAG0AGAXUVFvVjPPQB2nEAA9EARnEAWAJwA6ABxTxClgFZxagOzaAzFIA0IAJ4SFuxeekspu6bq0OAvk6NoseRnMz4+ZAVAAkgJ++KgAKpQ09IykUXQMRKwcSCDcvPxCqWII4jJaCnIATLa6arosWiwqRqYIALRaTXIy9hVqLEVdugoubhg4BERysES0waGoZABe-lDEycLpfoLCOUXVAGxyLCw9HZtayo5FtYj1Uo7FTVLqmjI9UkV9IO6DXqNgtACCAMbkADcSItUstMmtEBtNHJDuImjZOroSlozg0SlI5HDNixNnoZGoitoXm9PMNfgALMC-ADWeC+ACUAK4CSJUBbsJY8FZZUA5PKbQpwuFPAVPB7iVH1OyYp7iTb6cTVMqdTbEgaksByClU2ljJksqILcQpLhc8HZSGE+S3ZQdXStAVqSXS6RFOUKpUdIqq1yvdVDTU+PwBABi6AATvrWSRyFQEowQaaMmRVhaEF01Go5BoyloZAoShV1JKis11BsC9ItCU1GqPAGRnrmSEAuyTWkzSmeaJIdUs4dtPkCgKbDJUUUZPIlOU1LdMxOuj7+vWPnr8AI25zk6neb3oQPHHKZFJZKXUeVCk10XndJtNJ06+9hkHyKGIwBRAQQaNxOMxJIcqCnY7j26Z2FomL3h0J7ynCqJSEoMrYkUBZSLO3o6I+GpapSNJ0rQn7foaiYdtu3Z8jIhwwlocJqJsuJunkqJNNsdhqPmCiKgoDz6FhDbanhYyET+TDGlu3IQmBbo7OouKinsCgKMxWisfcilcTxUh8auXwhv4ZCwOScybkBZGSVC2y7D0JTiEUPSKboqKSIpMJ0SpJSqDRLC9L6JINi+ABCGA0nM+HEMyABGwXUiRYJduZVk7Kod5KLKuJOdiFjVFUmzioedHac+vi4EF6AhQEYXhl8qD4MYsXAeRkJ0VmtmUbemiaDRTomBIFQQbe+biA4KkdJIhWBsVpXlVAYVUBAZD4HpISGZA9VmWmGbbIquj6Pi3F5sWPW5LILCYjiDzeQoo0FON3iTdFoVjCZSYSWmR5FIoTzIgoNGSBKR0dVmlS2OxmaUd5S5+iuRV8FN1KPV8RrtnFIF8jlci2Ni0jytWGgogDmwnnI9rej0cJtbdny0AAwugFC0KgYCvvMa2vbuuT5BitoVMenPyk5+IWJxPRNPajqyJTYy0-TjPM0j4nmuzdlSJZdGlvk+aXP9dR5OUihDT9jiUVdta+f6Xi0OGZVgJAcwAHJgCIuBhaziugdjGKuopE76A4O3jpUEHqBrJMnqoui3S+YbhgAsrbi36YZxBzQtS0GZSECu-FG1IqdlEaAomyk5O+N1F0RzZuYrSlN6imR8V0dx-NCfLeSyfx2nK2Z2Jpls6BtcfUXEM6GoV1wUdGbiIoRd0Tt9GEz6voCOgEBwMIfmMAr2fs9IcoyihBYPLYjiOUd9SURiVTqNxd6zkNkMbzDzMTOQYTRvGRBb6jEjHh93EqG6VoMgNiyElIbYmLFvQqEkCNSWXwX4LSmLMAIX9GocyuPkQks4CR5DHGfW+xRuLeX5LlbiMg4E-H+GQIEqDJK4IxFdPMmU7BH2dHROQ7l9ACnlNiTMt0BK6gZMyaMtC3ptUxK0BccJvT5klENKejgjgFHxOdPMptlxPgmsGKA0coxRFEUrGyxMJz4nzASCcaE5HSkUZcbiBwNY+Q0dhKm+oWxQAMe7fIFg7J7HojYRwtinJ7GzNApEKE9gyE6OQs20NNQuPXB49YiVuJWFxF1Momx4LQkVJUPGk46LeS0jEzRd1tHR2Evo3ubt1gVA+kcW4gCWLcVRPKeQyEuE7VLEXfhuFBEES-CIqp29PHyhkoSIa5YBQ9BafaGEiIVadPcuIChncjIoKGd-dBWYdBPEqDxasDxUSaxaOUTouw0Ig2iU4-y90yrwwqmMRJP8qiYhUpOZEuVDhKQBreQoCFC4TmxCrABFDpYMyZnMJ5HMCQwmkexXYuIeJOSOFmTi9EdD5EUrY26ltra2wCA7J2+EoW6w+iwC+pZbjeXYqfMu5KIIPELseWQc58j1z4I3DuidyRQq6IXFo5KQGpOAd1OlFdygOGxDww4OJbrIG5ZAXlDhUUPHnDRO8exxwninneHadgBQOj4S4JwQA */
    id: "flowState",
    initial: "waitInitialPlay",
    context: ({ input }) => ({
      flowFlexibility: input.flowFlexibility,
      currentTime: input.initialFlowState.currentTime,
      currentSectionIndex: input.initialFlowState.currentSectionIndex,
      currentStepIndex: input.initialFlowState.currentStepIndex,
      flow: input.flow,
      eventQueue: [],
      flowKey: input.flow.key,
    }),
    on: {
      timeUpdate: {
        actions: "setCurrentTime",
      },
    },

    states: {
      waitInitialPlay: {
        on: {
          mediaPlaying: {
            target: "stepInitializing",
          },
        },
      },

      waitingInitialTimeUpdate: {
        on: {
          timeUpdate: {
            actions: "setCurrentTime",
            target: "stepInitializing",
          },
        },
      },

      stepInitializing: {
        entry: "initStep",
        always: {
          target: "stepActive",
        },
      },

      stepActive: {
        always: [
          {
            target: "checkStepRunTime",
          },
        ],
      },

      checkStepRunTime: {
        always: [
          {
            guard: "isStepRunTime",
            target: "stepRunning",
          },
          {
            target: "waitingForRunTime",
          },
        ],
      },

      waitingForRunTime: {
        on: {
          timeUpdate: [
            {
              actions: "setCurrentTime",
              target: "checkStepRunTime",
            },
          ],
        },
      },

      stepRunning: {
        entry: "runStep",
        always: {
          target: "stepRan",
        },
      },

      stepRan: {
        always: [
          {
            target: "checkStepEndTime",
          },
        ],
      },

      waitingForEndTime: {
        on: {
          timeUpdate: [
            {
              actions: "setCurrentTime",
              target: "checkStepEndTime",
            },
          ],
        },
      },

      checkStepEndTime: {
        always: [
          {
            guard: "isStepEndTime",
            target: "stepFinishing",
          },
          {
            target: "waitingForEndTime",
          },
        ],
      },

      stepFinishing: {
        entry: "finishStep",
        always: {
          target: "waitBlockingStep",
        },
      },

      waitBlockingStep: {
        always: [
          {
            guard: "isBlockingStep",
            actions: "waitBlockingStep",
          },
          {
            target: "stepCompleting",
          },
        ],
        on: {
          replay: {
            target: "stepInitializing",
          },
          mediaFinished: {
            description:
              "Media finished event can be dispatched when there is a blocking step at the end of the section. We should defer this event to handle it later.",
            actions: "deferMediaFinishedEvent",
          },
        },
      },

      stepCompleting: {
        entry: "disposeStep",
        always: [
          {
            guard: "hasNextStep",
            target: "proceedingNextStep",
          },
          {
            guard: "isLastStepInSection",
            target: "waitForMediaFinish",
          },
        ],
      },

      proceedingNextStep: {
        always: {
          actions: "proceedNextStep",
          target: "stepInitializing",
        },
      },

      waitForMediaFinish: {
        description: "",
        entry: "drainDeferredMediaFinishedEvents",
        on: {
          mediaFinished: [
            {
              guard: "isLastSection",
              target: "finished",
            },
            {
              actions: "proceedNextSection",
              target: "waitingInitialTimeUpdate",
            },
          ],
        },
      },

      finished: {
        entry: "finish",
        type: "final",
      },
    },
  });

  return createActor(flowStateMachine, { input });
};

export const createFlowStateStream = (machine: ReturnType<typeof createFlowStateActor>) => fromStream(machine);
